import { signal } from '@preact/signals'
import type { ReadonlySignal } from '@preact/signals'

/** A page of a shop app. The home page is always at the bottom of the stack. */
export type ShopPage =
  | { readonly kind: 'home' }
  | { readonly kind: 'section'; readonly section: string }
  | { readonly kind: 'basket' }
  | { readonly kind: 'orders' }
  | { readonly kind: 'order'; readonly order: number }

/** How the last change moved: a page in front (`push`) or one taken away (`pop`). */
export type NavMotion = 'push' | 'pop' | 'none'

/** One app's back stack on one device. */
export interface ShopNav {
  readonly stack: ReadonlySignal<readonly ShopPage[]>
  readonly motion: ReadonlySignal<NavMotion>
  push(page: ShopPage): void
  /** Back one page: false on the home page. */
  pop(): boolean
  /** Home with `page` on top (after checkout: the order's tracker). */
  reset(page?: ShopPage): void
}

const HOME: ShopPage = { kind: 'home' }

export function createNav(): ShopNav {
  const stack = signal<readonly ShopPage[]>([HOME])
  const motion = signal<NavMotion>('none')
  return {
    stack,
    motion,
    push(page) {
      motion.value = 'push'
      stack.value = [...stack.value, page]
    },
    pop() {
      if (stack.value.length <= 1) return false
      motion.value = 'pop'
      stack.value = stack.value.slice(0, -1)
      return true
    },
    reset(page) {
      motion.value = page ? 'push' : 'pop'
      stack.value = page ? [HOME, page] : [HOME]
    },
  }
}
